import { Box, ButtonBase, LinearProgress, Skeleton, Typography } from "@mui/material";
import { BreakdownGroup, BreakdownItem, formatNaira } from "services/finance";

const BreakdownCard = ({
  title,
  subtitle,
  group,
  color,
  isLoading,
  showTotal,
  emptyText,
  onSelect,
}: {
  title: string;
  subtitle: string;
  group?: BreakdownGroup;
  color: string;
  isLoading: boolean;
  showTotal?: string;
  emptyText: string;
  onSelect?: (item: BreakdownItem) => void;
}) => {
  const items = group?.items || [];

  return (
    <Box
      bgcolor="#FFFFFF"
      border="1px solid #E4E7EC"
      borderRadius="12px"
      p="24px"
      height="100%"
      boxSizing="border-box"
    >
      <Typography fontSize={16} fontWeight={600} color="#101828">
        {title}
      </Typography>
      <Typography fontSize={13} color="#667085" mb="20px">
        {subtitle}
      </Typography>

      {isLoading ? (
        [0, 1, 2, 3].map((i) => <Skeleton key={i} height={40} />)
      ) : items.length === 0 ? (
        <Typography fontSize={14} color="#98A2B3" py="24px" textAlign="center">
          {emptyText}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap="16px">
          {items.map((item) => (
            <ButtonBase
              key={item.category}
              component="div"
              disabled={!onSelect}
              onClick={() => onSelect?.(item)}
              sx={{
                display: "block",
                textAlign: "left",
                // Negative margin keeps the rows aligned while giving the hover
                // background some breathing room.
                m: "-8px",
                p: "8px",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            >
              <Box display="flex" justifyContent="space-between" gap="12px" mb="6px">
                <Typography
                  fontSize={14}
                  color="#101828"
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {item.label}
                </Typography>
                <Typography fontSize={14} fontWeight={600} color="#101828" flexShrink={0}>
                  {formatNaira(item.amount)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(Math.max(item.sharePercent || 0, 0), 100)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#EAECF0",
                  "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
                }}
              />
            </ButtonBase>
          ))}
        </Box>
      )}

      {showTotal && !isLoading && (
        <Box
          mt="24px"
          bgcolor="#F2F4F7"
          borderRadius="8px"
          p="16px"
          display="flex"
          justifyContent="space-between"
        >
          <Typography fontSize={14} fontWeight={600} color="#101828">
            {showTotal}
          </Typography>
          <Typography fontSize={14} fontWeight={600} color={color}>
            {formatNaira(group?.total)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BreakdownCard;
